import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { ArrowLeft, ChevronDown, ChevronUp, Plus, Trash2, Users as UsersIcon } from "react-feather";
import {
  useGetCustomerQuery,
  useReplaceCustomerAssignmentsMutation,
  useSetAssignmentStatusMutation,
} from "../customersApi";
import { useGetAreasQuery } from "../../areas/areasApi";
import { useGetUpcomingDeploymentsQuery } from "../../../guarding/guardingApi";
import CreateAssignmentsFromDeploymentModal from "../../../guarding/components/CreateAssignmentsFromDeploymentModal";
import { requestKey } from "../../../guarding/pendingRequest";
import { useCanEditGuardAssignments } from "../useGuardPermissions";
import { paths } from "../../../../routes/paths";

const DAY_NIGHT_OPTIONS = ["Day", "Night", "Both"];

// Matches the legacy Frm_Customer's Guarding Assignments tab: ticking a
// brand-new post's Active box immediately opened the same Permanent/Temp +
// From/To date panel used for later deactivation - so a post's activation
// window is captured right here at creation time, not left blank until
// someone later deactivates it. Permanent only needs a From date; Temp needs
// both From and To (direct instruction, not the legacy's day-count field).
const makeEmptyPost = (locationId = "") => ({
  assignmentName: "",
  assignmentArea: "",
  dayNight: "Day",
  locationId,
  isTemp: false,
  effectiveDate: dayjs().format("YYYY-MM-DD"),
  tempEndDate: dayjs().add(30, "day").format("YYYY-MM-DD"),
});

// Dedicated screen (own route, reached by a button - never a tab or an
// expand panel) mirroring texas_master's "Guarding Assignments" tab from
// Frm_Customer, now against the live Milestone 3 + Guard Assignments
// follow-up endpoints (handoff §9.7). Adding/removing posts uses the
// full-list diff PUT .../assignments (assignmentId is never client-sent -
// always server-generated); activating/deactivating one post uses the
// dedicated POST .../assignments/{assignmentId}/status endpoint, which
// carries its own effective-date/permanent-vs-temp window - the modal this
// restores.
//
// Multi-location customers (handoff §19.6/§9.7 update, added 2026-08-05):
// every assignment now belongs to a specific site (locationId/siteLabel).
// For the common single-location customer this screen is visually
// unchanged - no location picker, no grouping, one flat table, same as
// before. Only once a customer has more than one location does a Location
// picker appear on the add-post form and the table group by site - saving
// then issues one location-scoped PUT per group instead of one flat PUT
// (verified live: a location-scoped PUT only ever touches that location's
// own rows, so this can't accidentally wipe another site's assignments).
const GuardingAssignmentsPage = () => {
  const { branchId, custId } = useParams();
  const navigate = useNavigate();
  const canEdit = useCanEditGuardAssignments();
  const { data: detail, isLoading, isError, error } = useGetCustomerQuery({ branchId, custId });
  // Areas are branch-wise (revised 2026-08-13) and collide by name across
  // branches (e.g. "TOWN" exists in 3 different branches) - scope to this
  // customer's own branch, not the header picker, or the dropdown would show
  // every branch's areas mixed together with the same name repeated.
  const { data: areas = [] } = useGetAreasQuery({ branchId });
  const [replaceAssignments, { isLoading: isSaving }] = useReplaceCustomerAssignmentsMutation();
  const [setAssignmentStatus, { isLoading: isTogglingStatus }] = useSetAssignmentStatusMutation();

  // `assignments` always mirrors the server's own list - never mixed with
  // unsaved rows. Newly-added-but-not-yet-saved posts live in their own
  // `draftPosts` array instead (fix, 2026-08-17: previously new posts were
  // pushed straight into `assignments`, so ANY refetch of this customer -
  // including one triggered by an unrelated mutation elsewhere on the same
  // page, e.g. toggling a different post's Active checkbox - silently wiped
  // out never-saved posts via the effect below, which just overwrote
  // `assignments` wholesale with whatever the server currently had. Keeping
  // drafts in a separate state the server-sync effect never touches makes
  // that class of data loss structurally impossible, not just less likely).
  const [assignments, setAssignments] = useState([]);
  const [draftPosts, setDraftPosts] = useState([]);
  // Area corrections on already-saved posts, keyed by recordGuid (2026-09-09).
  // The office sometimes records the wrong area and confirms the real one
  // later, so this must be correctable in place rather than by deactivating
  // and re-posting - the assignment keeps its identity, history and guards.
  // Held apart from `assignments` (RTK Query cache, never mutated) and applied
  // when the PUT payload is built.
  const [areaEdits, setAreaEdits] = useState({});
  // The pending deployment currently being turned into posts, if any.
  const [deploymentToCreate, setDeploymentToCreate] = useState(null);
  const [post, setPost] = useState(() => makeEmptyPost());

  // Confirmed orders with guards still to deploy, narrowed to this customer.
  // custId only became available on this feed in Batch148 - before that the row
  // carried the customer NAME only, which is not a safe key (cust_id is
  // branch-local, so name matching would cross branches).
  const { data: pendingDeployments = [] } = useGetUpcomingDeploymentsQuery(undefined, { skip: !canEdit });
  const pendingForCustomer = pendingDeployments.filter(
    (o) => String(o.custId) === String(custId) && String(o.branchId) === String(branchId)
  );
  const locations = detail?.locations || [];
  const hasMultipleLocations = locations.length > 1;
  const primaryLocationId = locations.find((l) => l.isPrimary)?.locationId ?? locations[0]?.locationId ?? null;
  const [deactivating, setDeactivating] = useState(null); // the assignment row being deactivated
  const [effectiveDate, setEffectiveDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [isTemp, setIsTemp] = useState(false);
  const [tempEndDate, setTempEndDate] = useState(dayjs().add(30, "day").format("YYYY-MM-DD"));
  // Inactive posts are historical - shown separately, collapsed by default,
  // and never editable from this screen (no Active checkbox, no remove).
  // New posts (handleAddPost) always start isActive: 1, so they land in the
  // Active section automatically with no extra wiring needed.
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    if (detail?.assignments) setAssignments(detail.assignments);
  }, [detail]);

  // Warn on tab close/refresh while a post has been added but not yet saved
  // - same fix as above, applied to the one exit path in-app navigation
  // guards below can't intercept.
  // Area corrections count as unsaved work too - losing a correction silently
  // is the same harm as losing a new post.
  const unsavedCount = draftPosts.length + Object.keys(areaEdits).length;

  useEffect(() => {
    if (unsavedCount === 0) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [unsavedCount]);

  const confirmLeaveIfUnsaved = () => {
    if (unsavedCount === 0) return true;
    return window.confirm(
      `You have ${unsavedCount} unsaved guarding change${unsavedCount > 1 ? "s" : ""} ` +
        `(new posts or area corrections) that ${unsavedCount > 1 ? "have" : "has"} not been saved yet. ` +
        `Leave this page without saving?`
    );
  };

  if (isError) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.customers} className="d-inline-flex align-items-center mb-3">
            <ArrowLeft size={16} className="me-1" /> Back to Customers
          </Link>
          <div className="alert alert-danger">
            Could not load this customer ({error?.message || "server error"}).
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !detail) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const customer = detail.customer;

  const handleAddPost = () => {
    if (!post.assignmentName.trim() || !post.assignmentArea) {
      toast.error("Post name and area are required");
      return;
    }
    if (!post.effectiveDate) {
      toast.error("From date is required");
      return;
    }
    if (post.isTemp && !post.tempEndDate) {
      toast.error("To date is required for a temporary post");
      return;
    }
    // "Both" isn't a real per-row value the guard-deployment side of the
    // legacy app tracks separately for Day vs Night coverage - the server's
    // `day_night` column only ever holds "Day"/"Night" for an actual shift.
    // Picking "Both" here is shorthand for "post one guard for Day and
    // another for Night", so it fans out into two separate draft rows.
    //
    // Fix, 2026-08-20: the two rows used to share the exact same
    // assignmentName/assignmentArea (differing only in dayNight), and the
    // API's replace-assignments reconciliation only ever created one of
    // them - reported by a user as "Both is not creating two assignments."
    // Suffixing the name (" - Day"/" - Night") makes the two rows
    // unambiguously distinct on every field the server could plausibly
    // match new rows by, independent of how that reconciliation works.
    const buildDraft = (dayNight, nameSuffix = "") => ({
      recordGuid: null,
      assignmentName: `${post.assignmentName.trim()}${nameSuffix}`,
      assignmentArea: post.assignmentArea,
      dayNight,
      isActive: 1,
      locationId: post.locationId || primaryLocationId,
      // No assignmentId yet (server-generated on save) - these three ride
      // along on the local row so handleSaveList can set the real
      // activation window in a follow-up call once the row is created.
      effectiveDate: post.effectiveDate,
      isTemp: post.isTemp,
      tempEndDate: post.isTemp ? post.tempEndDate : null,
    });
    const newDrafts =
      post.dayNight === "Both"
        ? [buildDraft("Day", " - Day"), buildDraft("Night", " - Night")]
        : [buildDraft(post.dayNight)];
    setDraftPosts((prev) => [...prev, ...newDrafts]);
    setPost((prev) => makeEmptyPost(prev.locationId));
  };

  const removeDraft = (index) => {
    setDraftPosts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveList = async () => {
    const editedGuids = Object.keys(areaEdits);
    if (draftPosts.length === 0 && editedGuids.length === 0) return;
    // A PUT is needed for any location that has a new draft post OR a
    // corrected area on an existing post. Locations with neither are left
    // alone - an already-saved post can no longer be removed from this screen
    // (see the Trash2-button note below), so there is nothing else to send.
    const affectedLocationIds = new Set([
      ...draftPosts.map((d) => d.locationId ?? primaryLocationId),
      ...assignments
        .filter((a) => a.recordGuid && areaEdits[a.recordGuid] !== undefined)
        .map((a) => a.locationId ?? primaryLocationId),
    ]);
    try {
      for (const locationId of affectedLocationIds) {
        const existingForLocation = assignments.filter((a) => (a.locationId ?? primaryLocationId) === locationId);
        const draftsForLocation = draftPosts.filter((d) => (d.locationId ?? primaryLocationId) === locationId);
        // eslint-disable-next-line no-await-in-loop
        const result = await replaceAssignments({
          branchId,
          custId,
          locationId,
          assignments: [...existingForLocation, ...draftsForLocation].map((a) => ({
            recordGuid: a.recordGuid ?? null,
            assignmentName: a.assignmentName,
            // A corrected area wins over the stored one. The upsert behind this
            // endpoint matches on RecordGuid and UPDATEs assmnt_area in place,
            // so the post keeps its identity rather than being recreated.
            assignmentArea: (a.recordGuid && areaEdits[a.recordGuid]) || a.assignmentArea,
            dayNight: a.dayNight,
            isActive: Number(a.isActive) ? 1 : 0,
          })),
        }).unwrap();

        // The replace-list endpoint has no fields for the activation window
        // (effectiveDate/isTemp/tempEndDate) - only the dedicated status
        // endpoint does, and that needs a real assignmentId, which only
        // exists once this PUT creates the row. Every brand-new post
        // carries the Permanent/Temp + date window chosen on the Add form,
        // so it needs matching to its counterpart in this PUT's own
        // response, which already carries the server-generated assignmentId
        // for every row (including brand-new ones).
        //
        // Fix, 2026-08-17: this used to match by name+area+dayNight, which
        // silently failed to set the window (no error surfaced) whenever
        // that match missed - and per `sp_master_customer_assignment_upsert`
        // (Batch35_CustomerMultiLocation.sql), a brand-new row has NO
        // activation-window row at all until this follow-up call succeeds,
        // so a missed match meant the post came back with no Effective date
        // shown at all - reported by a user as "the last date isn't saved"
        // for a temp post added for a week. Worse, on a false *positive*
        // match it could silently write the new window onto a different,
        // unrelated existing post instead. Every row in the PUT response
        // (new or existing) carries a real `recordGuid` - existing rows
        // update in place by the same `recordGuid` we sent, so any response
        // row whose `recordGuid` isn't among the ones we already knew about
        // is unambiguously one of this save's new rows - no name/area/
        // dayNight comparison, and therefore no risk of a same-named post
        // silently swallowing or stealing another one's window.
        const responseRows = result?.assignments || [];
        const knownRecordGuids = new Set(existingForLocation.map((a) => a.recordGuid).filter(Boolean));
        const newResponseRows = responseRows.filter((r) => r.recordGuid && !knownRecordGuids.has(r.recordGuid));
        for (let i = 0; i < draftsForLocation.length; i += 1) {
          const a = draftsForLocation[i];
          const target = newResponseRows[i];
          if (!target) continue;
          // eslint-disable-next-line no-await-in-loop
          await setAssignmentStatus({
            branchId,
            custId,
            assignmentId: target.assignmentId,
            isActive: true,
            effectiveDate: dayjs(a.effectiveDate || dayjs()).format("YYYY-MM-DDT00:00:00"),
            isTemp: Boolean(a.isTemp),
            tempEndDate: a.isTemp ? dayjs(a.tempEndDate).format("YYYY-MM-DDT00:00:00") : null,
          }).unwrap();
        }
      }
      // The mutations above already invalidate this customer's query tag,
      // so `assignments` will pick up the newly-saved posts (with real
      // assignmentIds) on the resulting refetch - clearing drafts here just
      // stops them rendering a second time alongside that refreshed data.
      setDraftPosts([]);
      // Area corrections are now persisted; drop the local overrides so the
      // refetched rows are the single source of truth again.
      setAreaEdits({});
      toast.success("Guarding assignments saved");
    } catch (err) {
      toast.error(err?.message || "Could not save assignments");
    }
  };

  const openDeactivateModal = (assignment) => {
    setDeactivating(assignment);
    setEffectiveDate(dayjs().format("YYYY-MM-DD"));
    setIsTemp(false);
    setTempEndDate(dayjs().add(30, "day").format("YYYY-MM-DD"));
  };

  const handleToggleActive = async (assignment, checked) => {
    if (!checked) {
      openDeactivateModal(assignment);
      return;
    }
    // Reactivating doesn't need the date window in the source screen -
    // only deactivation opens the panel there.
    try {
      await setAssignmentStatus({
        branchId,
        custId,
        assignmentId: assignment.assignmentId,
        isActive: true,
        effectiveDate: dayjs().format("YYYY-MM-DDT00:00:00"),
        isTemp: false,
        tempEndDate: null,
      }).unwrap();
      toast.success("Assignment activated");
    } catch (err) {
      toast.error(err?.message || "Could not update assignment");
    }
  };

  // `row` is either a draft ({ a, isDraft: true, draftIndex }) or an
  // already-saved row ({ a, isDraft: false }). Draft rows get an inline
  // style (not just a Bootstrap class) for the highlight, since this
  // template's own table/row CSS was overriding the plain `table-warning`
  // class with a higher-specificity background - the previous highlight
  // was silently invisible even though the class was applied correctly.
  const renderAssignmentRow = ({ a, isDraft, draftIndex }, key) => (
    <tr key={key} style={isDraft ? { backgroundColor: "#fff3cd" } : undefined}>
      <td>
        {a.assignmentName}
        {isDraft && <span className="badge badge-linewarning ms-2">Unsaved</span>}
      </td>
      {/* Area is correctable in place on a SAVED post (2026-09-09) - the office
          sometimes records the wrong area and confirms the real one later.
          Chosen from the Area master only, never typed: the supervisor-area
          assignment and the attendance dashboard both match this value by NAME
          against area_master, so free text here would silently drop the post
          out of those screens. A draft row keeps its plain text - its area was
          just picked on the Add form above. */}
      <td>
        {isDraft || !canEdit || !a.recordGuid ? (
          a.assignmentArea
        ) : (
          <select
            className="form-control form-control-sm"
            value={areaEdits[a.recordGuid] ?? a.assignmentArea ?? ""}
            onChange={(e) =>
              setAreaEdits((prev) => {
                const next = { ...prev };
                // Selecting the original value back is not an edit - drop it so
                // Save Changes does not PUT a location for no reason.
                if (e.target.value === (a.assignmentArea ?? "")) delete next[a.recordGuid];
                else next[a.recordGuid] = e.target.value;
                return next;
              })
            }
          >
            {/* The stored value may predate the current Area master (or have
                been free text) - keep it selectable so opening the dropdown
                never silently blanks an existing area. */}
            {a.assignmentArea && !areas.some((ar) => ar.areaName === a.assignmentArea) && (
              <option value={a.assignmentArea}>{a.assignmentArea} (not in Area master)</option>
            )}
            {areas.map((area) => (
              <option key={area.areaId} value={area.areaName}>{area.areaName}</option>
            ))}
          </select>
        )}
        {a.recordGuid && areaEdits[a.recordGuid] !== undefined && (
          <span className="badge badge-linewarning ms-1">Unsaved</span>
        )}
      </td>
      <td>{a.dayNight}</td>
      <td>
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            checked={Boolean(Number(a.isActive))}
            onChange={(e) => handleToggleActive(a, e.target.checked)}
            disabled={!canEdit || isDraft || isTogglingStatus}
          />
        </div>
      </td>
      <td>
        {a.effectiveDate
          ? `${dayjs(a.effectiveDate).format("DD-MMM-YYYY")}${
              a.isTemp && a.tempEndDate ? ` to ${dayjs(a.tempEndDate).format("DD-MMM-YYYY")}` : ""
            }`
          : "-"}
      </td>
      {canEdit && (
        <td>
          {/* Only an unsaved draft row (just added this session, not yet
              part of a Save Changes click) can be removed outright - a
              persisted active assignment has no delete action at all,
              deactivate is the only way to end one (direct instruction). */}
          {isDraft && (
            <button type="button" className="btn btn-link p-0 text-danger" onClick={() => removeDraft(draftIndex)}>
              <Trash2 size={14} />
            </button>
          )}
        </td>
      )}
    </tr>
  );

  // Inactive rows are historical - no Active checkbox, no remove action,
  // read-only regardless of canEdit.
  const renderInactiveRow = ({ a, key }) => (
    <tr key={key}>
      <td>{a.assignmentName}</td>
      <td>{a.assignmentArea}</td>
      <td>{a.dayNight}</td>
      <td>
        {a.effectiveDate
          ? `${dayjs(a.effectiveDate).format("DD-MMM-YYYY")}${
              a.isTemp && a.tempEndDate ? ` to ${dayjs(a.tempEndDate).format("DD-MMM-YYYY")}` : ""
            }`
          : "-"}
      </td>
    </tr>
  );

  // Draft (unsaved) posts are listed ahead of already-saved ones, matching
  // the previous "unsaved rows sort to top" behavior - now a plain
  // array-order fact (drafts come from a wholly separate array) rather than
  // a sort keyed on assignmentId presence.
  const activeAssignments = [
    ...draftPosts.map((a, draftIndex) => ({ a, isDraft: true, draftIndex, key: `draft-${draftIndex}` })),
    ...assignments
      .map((a, index) => ({ a, isDraft: false, key: a.recordGuid || `${a.assignmentId}-${index}` }))
      .filter(({ a }) => Boolean(Number(a.isActive))),
  ];
  const inactiveAssignments = assignments
    .map((a, index) => ({ a, isDraft: false, key: a.recordGuid || `${a.assignmentId}-${index}` }))
    .filter(({ a }) => !Number(a.isActive));

  // Group by site only once a customer actually has more than one location -
  // for the common single-location case the table stays exactly as it was
  // before this feature, no grouping header. Shared by both the Active and
  // Inactive sections.
  const buildGroupedRows = (rows) =>
    hasMultipleLocations
      ? [...locations]
          .sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0))
          .map((loc) => ({
            location: loc,
            rows: rows.filter(({ a }) => (a.locationId ?? primaryLocationId) === loc.locationId),
          }))
          .filter((group) => group.rows.length > 0)
      : null;

  const activeGroupedRows = buildGroupedRows(activeAssignments);
  const inactiveGroupedRows = buildGroupedRows(inactiveAssignments);

  const confirmDeactivate = async () => {
    try {
      await setAssignmentStatus({
        branchId,
        custId,
        assignmentId: deactivating.assignmentId,
        isActive: false,
        effectiveDate: dayjs(effectiveDate).format("YYYY-MM-DDT00:00:00"),
        isTemp,
        tempEndDate: isTemp ? dayjs(tempEndDate).format("YYYY-MM-DDT00:00:00") : null,
      }).unwrap();
      toast.success("Assignment deactivated");
      setDeactivating(null);
    } catch (err) {
      toast.error(err?.message || "Could not deactivate assignment");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header flex-wrap">
          <div className="add-item d-flex" style={{ minWidth: 0 }}>
            <div className="page-title" style={{ maxWidth: 700 }}>
              <Link
                to={paths.customerEdit(branchId, custId)}
                className="d-inline-flex align-items-center mb-2 text-truncate"
                style={{ maxWidth: "100%" }}
                title={customer.custName}
                onClick={(e) => {
                  if (!confirmLeaveIfUnsaved()) e.preventDefault();
                }}
              >
                <ArrowLeft size={16} className="me-1 flex-shrink-0" /> Back to {customer.custName}
              </Link>
              <h4>Guarding Assignments</h4>
              <h6 style={{ wordBreak: "break-word" }}>{customer.custName}</h6>
            </div>
          </div>
          <div className="page-btn">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => {
                if (!confirmLeaveIfUnsaved()) return;
                navigate(paths.customerBulkGuards(branchId, custId));
              }}
            >
              <UsersIcon size={16} className="me-2" />
              Bulk Entry
            </button>
          </div>
        </div>

        {!canEdit && (
          <div className="alert alert-info">
            You have read-only access. Editing assignments requires the Deployment.Edit permission.
          </div>
        )}

        {/* Confirmed orders for THIS customer whose guards are not deployed yet
            (2026-09-09). Everything needed to raise the posts is already on the
            order, so this saves retyping it here and then matching it back up by
            hand. Day and Night always come out as separate posts. */}
        {canEdit && pendingForCustomer.length > 0 && (
          <div className="card mb-4 border-warning">
            <div className="card-body">
              <h5 className="mb-1">Pending Deployments</h5>
              <p className="text-muted small mb-3">
                {pendingForCustomer.length} confirmed order
                {pendingForCustomer.length === 1 ? "" : "s"} for this customer with guards not yet deployed.
              </p>
              {/* One row per deployment request (Batch161): an order's lines
                  sharing a start date and guard service, so an order can
                  appear more than once. */}
              {pendingForCustomer.map((o) => (
                <div
                  key={requestKey(o)}
                  className="d-flex flex-wrap align-items-center justify-content-between gap-2 border rounded p-2 mb-2"
                >
                  <div>
                    <strong>{o.orderNumber}</strong>
                    <span className="text-muted ms-2">
                      {o.guardsCount} guard{o.guardsCount === 1 ? "" : "s"}
                      {o.serviceTypeName ? ` · ${o.serviceTypeName}` : ""}
                      {o.area ? ` · ${o.area}` : ""}
                      {o.requestedDeploymentDate
                        ? ` · wanted ${dayjs(o.requestedDeploymentDate).format("DD MMM YYYY")}`
                        : ""}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => setDeploymentToCreate(o)}
                  >
                    Create Assignments
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            {canEdit && (
              <>
                <h5 className="mb-3">Add Guarding Post</h5>
                <div className="row">
                  {hasMultipleLocations && (
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Location</label>
                      <select
                        className="form-control"
                        value={post.locationId || primaryLocationId || ""}
                        onChange={(e) => setPost((prev) => ({ ...prev, locationId: Number(e.target.value) }))}
                      >
                        {locations.map((loc) => (
                          <option key={loc.locationId} value={loc.locationId}>
                            {loc.siteLabel}{loc.isPrimary ? " (Primary)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Assignment Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={post.assignmentName}
                      onChange={(e) => setPost((prev) => ({ ...prev, assignmentName: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Area</label>
                    <select
                      className="form-control"
                      value={post.assignmentArea}
                      onChange={(e) => setPost((prev) => ({ ...prev, assignmentArea: e.target.value }))}
                    >
                      <option value="">--Select--</option>
                      {areas.map((area) => (
                        <option key={area.areaId} value={area.areaName}>{area.areaName}</option>
                      ))}
                    </select>
                    {areas.length === 0 && (
                      <small className="text-muted">
                        No areas set up yet - add them under HR Masters &gt; Areas first.
                      </small>
                    )}
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label">Day / Night</label>
                    <select
                      className="form-control"
                      value={post.dayNight}
                      onChange={(e) => setPost((prev) => ({ ...prev, dayNight: e.target.value }))}
                    >
                      {DAY_NIGHT_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3 mb-3">
                    <label className="form-label d-block">Duration</label>
                    <div className="d-flex gap-3" style={{ height: 38, alignItems: "center" }}>
                      <div className="form-check mb-0">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="add-post-permanent"
                          checked={!post.isTemp}
                          onChange={() => setPost((prev) => ({ ...prev, isTemp: false }))}
                        />
                        <label className="form-check-label" htmlFor="add-post-permanent">Permanent</label>
                      </div>
                      <div className="form-check mb-0">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="add-post-temp"
                          checked={post.isTemp}
                          onChange={() => setPost((prev) => ({ ...prev, isTemp: true }))}
                        />
                        <label className="form-check-label" htmlFor="add-post-temp">Temp</label>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label">From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={post.effectiveDate}
                      onChange={(e) => setPost((prev) => ({ ...prev, effectiveDate: e.target.value }))}
                    />
                  </div>
                  {post.isTemp && (
                    <div className="col-md-3 mb-3">
                      <label className="form-label">To Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={post.tempEndDate}
                        onChange={(e) => setPost((prev) => ({ ...prev, tempEndDate: e.target.value }))}
                      />
                    </div>
                  )}
                  <div className="col-md-3 mb-3 d-flex align-items-end">
                    <button type="button" className="btn btn-primary" onClick={handleAddPost}>
                      <Plus size={16} className="me-1" /> Add Post
                    </button>
                  </div>
                </div>
              </>
            )}

            {canEdit && draftPosts.length > 0 && (
              <div className="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-2 py-2 px-3">
                <span>
                  {draftPosts.length} unsaved guarding post{draftPosts.length > 1 ? "s" : ""} below (highlighted) -
                  nothing is saved until you click <strong>Save Changes</strong>.
                </span>
                <button type="button" className="btn btn-primary btn-sm" onClick={handleSaveList} disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            )}

            <h5 className="mb-3">Active Assignments</h5>
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Assignment</th>
                    <th>Area</th>
                    <th>Day / Night</th>
                    <th>Active</th>
                    <th>Effective</th>
                    {canEdit && <th />}
                  </tr>
                </thead>
                <tbody>
                  {activeGroupedRows
                    ? activeGroupedRows.map((group) => (
                        <React.Fragment key={group.location.locationId}>
                          <tr className="table-light">
                            <td colSpan={canEdit ? 6 : 5} className="fw-semibold">
                              {group.location.siteLabel}
                              {group.location.isPrimary && (
                                <span className="badge badge-linesuccess ms-2">Primary</span>
                              )}
                            </td>
                          </tr>
                          {group.rows.map((row) => renderAssignmentRow(row, row.key))}
                        </React.Fragment>
                      ))
                    : activeAssignments.map((row) => renderAssignmentRow(row, row.key))}
                  {activeAssignments.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-muted">No active guarding posts.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {canEdit && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveList}
                disabled={isSaving || (draftPosts.length === 0 && Object.keys(areaEdits).length === 0)}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            )}
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <button
              type="button"
              className="btn btn-link p-0 d-flex align-items-center text-decoration-none"
              onClick={() => setShowInactive((prev) => !prev)}
            >
              {showInactive ? <ChevronUp size={16} className="me-2" /> : <ChevronDown size={16} className="me-2" />}
              <h5 className="mb-0">Inactive Assignments ({inactiveAssignments.length})</h5>
            </button>

            {showInactive && (
              <div className="table-responsive mt-3">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Assignment</th>
                      <th>Area</th>
                      <th>Day / Night</th>
                      <th>Effective</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inactiveGroupedRows
                      ? inactiveGroupedRows.map((group) => (
                          <React.Fragment key={group.location.locationId}>
                            <tr className="table-light">
                              <td colSpan={4} className="fw-semibold">
                                {group.location.siteLabel}
                                {group.location.isPrimary && (
                                  <span className="badge badge-linesuccess ms-2">Primary</span>
                                )}
                              </td>
                            </tr>
                            {group.rows.map((row) => renderInactiveRow(row))}
                          </React.Fragment>
                        ))
                      : inactiveAssignments.map((row) => renderInactiveRow(row))}
                    {inactiveAssignments.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-muted">No inactive guarding posts.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal show={Boolean(deactivating)} onHide={() => setDeactivating(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Deactivate {deactivating?.assignmentName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Effective Date</label>
            <input type="date" className="form-control" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
          </div>
          <div className="d-flex gap-4 mb-3">
            <div className="form-check">
              <input type="radio" className="form-check-input" id="deact-permanent" checked={!isTemp} onChange={() => setIsTemp(false)} />
              <label className="form-check-label" htmlFor="deact-permanent">Permanent</label>
            </div>
            <div className="form-check">
              <input type="radio" className="form-check-input" id="deact-temp" checked={isTemp} onChange={() => setIsTemp(true)} />
              <label className="form-check-label" htmlFor="deact-temp">Temp</label>
            </div>
          </div>
          {isTemp && (
            <div className="mb-3">
              <label className="form-label">Temp End Date</label>
              <input type="date" className="form-control" value={tempEndDate} onChange={(e) => setTempEndDate(e.target.value)} />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeactivating(null)}>Cancel</Button>
          <Button variant="primary" onClick={confirmDeactivate} disabled={isTogglingStatus}>Ok</Button>
        </Modal.Footer>
      </Modal>

      {/* Shared with the Pending Deployments screen, so posts raised from either
          place come out identically - including Day and Night as separate rows. */}
      <CreateAssignmentsFromDeploymentModal
        show={Boolean(deploymentToCreate)}
        order={deploymentToCreate}
        onClose={() => setDeploymentToCreate(null)}
      />
    </div>
  );
};

export default GuardingAssignmentsPage;
